import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import styled from '@emotion/styled'

import { headerHeight, indexPageMaxContentWidth } from 'config/theme'
import NavGridText from 'components/common/GridItemWrapper/Desktop/NavGridText'
import NavGridImage from 'components/common/GridItemWrapper/Desktop/NavGridImage'
import NavPanelWithTab from './NavPanelWithTab'
import { useSelector } from 'react-redux'
import { selectNavbarClicked } from 'store/pageLoad/pageLoadSlice'

const InnerBox = styled(Box)`
  ${({ theme, $hovered }) => `
    cursor: pointer;
    text-align: center;

    &::after {
      content: '';
      width: 32px;
      height: 4px;
      display: block;
      visibility: ${$hovered ? 'visible' : 'hidden'};
      background: ${theme.palette.primary.main};
      margin: auto;
      border-radius: 10px;
      position: relative;
      top: 2px;
    }
  `}
`

const NavPanel = styled(Box)`
  ${({ theme, $hovered }) => `
    position: fixed;
    left: 0;
    right: 0;
    top: ${headerHeight};
    max-width: ${indexPageMaxContentWidth};
    margin: 0 36px;
    height: auto;
    max-height: calc(100vw * 0.35);
    overflow-y: auto;
    background-color: rgba(251, 251, 251);
    border: 1px solid ${theme.palette.grey.secondary};
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05), 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 24px;
    z-index: 50;
    opacity: ${$hovered ? 1 : 0};
    transition: all .2s linear;
    pointer-events: ${$hovered ? 'default' : 'none'};

    ${theme.breakpoints.up('xl')} {
      margin: 0 auto;
    }
  `}
`

const NavButton = ({
  children,
  navContent,
  className,
  onCatalogOrSubMenuClick,
  onServiceClassificationClick,
  ...rest
}) => {
  const [hovered, setHovered] = useState(false)
  const navbarClicked = useSelector(selectNavbarClicked)

  const isNavPanelWithTab = navContent?.subMenus?.length > 0
  const isTextMode = navContent?.displayMode === 'text'

  return (
    <Typography
      variant="body2"
      className={className}
      mx={1.5}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...rest}
    >
      <Box
        fontWeight="fontWeightBold"
        height={headerHeight}
        display="flex"
        alignItems="center"
      >
        <InnerBox px={2} pb={1} pt={2} $hovered={hovered}>
          {children}
        </InnerBox>
      </Box>

      {!!navContent &&
        (isNavPanelWithTab ? (
          <NavPanelWithTab
            $hovered={hovered && !navbarClicked}
            navContent={navContent}
            hasSubMenu={isNavPanelWithTab}
            onLinkClick={() => setHovered(false)}
            onCatalogOrSubMenuClick={onCatalogOrSubMenuClick}
            onServiceClassificationClick={onServiceClassificationClick}
            isTextMode={isTextMode}
          />
        ) : (
          <NavPanel $hovered={hovered && !navbarClicked}>
            <Box mb={12} mt={9} px={9}>
              {isTextMode ? (
                <NavGridText
                  content={navContent}
                  menuDataForGa={{
                    name: navContent.name,
                    _id: navContent?._id,
                  }}
                  hasSubMenu={isNavPanelWithTab}
                  onLinkClick={() => setHovered(false)}
                  onCatalogOrSubMenuClick={onCatalogOrSubMenuClick}
                  onServiceClassificationClick={onServiceClassificationClick}
                />
              ) : (
                <NavGridImage
                  content={navContent}
                  menuDataForGa={{
                    name: navContent.name,
                    _id: navContent?._id,
                  }}
                  hasSubMenu={isNavPanelWithTab}
                  onLinkClick={() => setHovered(false)}
                  onCatalogOrSubMenuClick={onCatalogOrSubMenuClick}
                  onServiceClassificationClick={onServiceClassificationClick}
                />
              )}
            </Box>
          </NavPanel>
        ))}
    </Typography>
  )
}

const StyledNavButton = styled(NavButton)`
  position: relative;
`

export default StyledNavButton
